import dayjs from "dayjs";

const RANGE_LABELS = {
  day: "Daily",
  week: "Weekly",
  month: "Monthly",
};

const getReports = (subscription = {}) =>
  Array.isArray(subscription.reports) ? subscription.reports.filter(Boolean) : [];

export const formatReportTypeName = (name = "") =>
  String(name)
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export const getSubscriptionReportName = (report = {}, reportTypes = []) => {
  const reportType = reportTypes.find((item) => item.id === report.reportTypeId);
  return formatReportTypeName(reportType?.name || report.reportName || report.reportTypeName || "Unknown Report");
};

export const getReportScopeLabel = (report = {}, reportTypes = []) => {
  const reportType = reportTypes.find((item) => item.id === report.reportTypeId);
  const level = reportType?.level || report.level;
  const branchCount = Array.isArray(report.branches) ? report.branches.length : 0;

  if (level === "organization" || level === "0") return "Organization";
  if (branchCount === 1) return "1 branch";
  if (branchCount > 1) return `${branchCount} branches`;
  return "Branch";
};

export const isSubscriptionActive = (subscription = {}) =>
  Number(subscription.activeStatus ?? 1) === 1;

const hasWideTableConfig = (report = {}) =>
  Array.isArray(report.renderConfig?.columns) && report.renderConfig.columns.length >= 7;

const hasCsvExport = (report = {}) => Boolean(report.exportConfig?.csv);

export const buildSubscriptionInsights = (subscriptions = [], reportTypes = []) => {
  const normalizedSubscriptions = subscriptions.filter(Boolean);
  const allReports = normalizedSubscriptions.flatMap(getReports);
  const activeCount = normalizedSubscriptions.filter(isSubscriptionActive).length;
  const pausedCount = normalizedSubscriptions.length - activeCount;
  const csvReportCount = allReports.filter(hasCsvExport).length;
  const wideTableCount = allReports.filter(hasWideTableConfig).length;
  const emptySubscriptions = normalizedSubscriptions.filter(
    (subscription) => getReports(subscription).length === 0
  );

  const rangeRows = ["month", "week", "day"]
    .map((range) => {
      const rangeSubscriptions = normalizedSubscriptions.filter(
        (subscription) => subscription.reportRange === range
      );
      return {
        range: RANGE_LABELS[range],
        subscriptions: rangeSubscriptions.length,
        reports: rangeSubscriptions.flatMap(getReports).length,
      };
    })
    .filter((row) => row.subscriptions || row.reports);

  const largestSubscriptions = [...normalizedSubscriptions]
    .sort((left, right) => getReports(right).length - getReports(left).length)
    .slice(0, 5)
    .map((subscription) => ({
      name: subscription.subscriptionName || "Untitled subscription",
      range: RANGE_LABELS[subscription.reportRange] || subscription.reportRange || "Unknown",
      reports: getReports(subscription).length,
      status: isSubscriptionActive(subscription) ? "Active" : "Paused",
    }));

  const notes = [];
  if (pausedCount) notes.push(`${pausedCount} subscription${pausedCount === 1 ? " is" : "s are"} paused.`);
  if (emptySubscriptions.length) {
    notes.push(
      `${emptySubscriptions.length} subscription${emptySubscriptions.length === 1 ? " has" : "s have"} no reports selected.`
    );
  }
  if (wideTableCount) {
    notes.push(`${wideTableCount} report${wideTableCount === 1 ? " uses" : "s use"} automatic landscape PDF output.`);
  }

  return {
    kpis: [
      { label: "Subscriptions", value: normalizedSubscriptions.length.toLocaleString() },
      { label: "Active", value: activeCount.toLocaleString() },
      { label: "Paused", value: pausedCount.toLocaleString() },
      { label: "Reports Selected", value: allReports.length.toLocaleString() },
      { label: "CSV Attachments", value: csvReportCount.toLocaleString() },
      { label: "Wide PDF Sections", value: wideTableCount.toLocaleString() },
    ],
    tables: [
      {
        title: "Schedule Mix",
        columns: [
          { id: "range", label: "Schedule" },
          { id: "subscriptions", label: "Subscriptions" },
          { id: "reports", label: "Reports" },
        ],
        rows: rangeRows,
      },
      {
        title: "Largest Subscriptions",
        columns: [
          { id: "name", label: "Name" },
          { id: "range", label: "Schedule" },
          { id: "reports", label: "Reports" },
          { id: "status", label: "Status" },
        ],
        rows: largestSubscriptions,
      },
    ].filter((table) => table.rows.length),
    notes,
  };
};

export const getSubscriptionPeriodLabel = (subscription = {}, dateRange = []) => {
  if (!Array.isArray(dateRange) || !dateRange.length) return "Select a period";

  const selectedDate = dayjs.unix(dateRange[0]);
  switch (subscription.reportRange) {
    case "year":
      return selectedDate.format("YYYY");
    case "day":
      return selectedDate.format("MMM D, YYYY");
    case "week":
      return `${selectedDate.startOf("week").format("MMM D")} to ${selectedDate
        .endOf("week")
        .format("MMM D, YYYY")}`;
    case "month":
      return selectedDate.format("MMMM YYYY");
    default:
      return "Selected period";
  }
};

export const buildPastReportDownloadInsights = (subscription, dateRange = [], reportTypes = []) => {
  if (!subscription) return null;

  const reports = getReports(subscription);
  const csvReportCount = reports.filter(hasCsvExport).length;
  const wideTableCount = reports.filter(hasWideTableConfig).length;
  const reportRows = reports.map((report) => ({
    report: getSubscriptionReportName(report, reportTypes),
    scope: getReportScopeLabel(report, reportTypes),
    output: hasCsvExport(report) ? "PDF + CSV" : "PDF",
  }));

  return {
    kpis: [
      { label: "Subscription", value: subscription.subscriptionName || "Untitled" },
      { label: "Schedule", value: RANGE_LABELS[subscription.reportRange] || subscription.reportRange || "Unknown" },
      { label: "Period", value: getSubscriptionPeriodLabel(subscription, dateRange) },
      { label: "Reports", value: reports.length.toLocaleString() },
      { label: "CSV Files", value: csvReportCount.toLocaleString() },
      { label: "Status", value: isSubscriptionActive(subscription) ? "Active" : "Paused" },
    ],
    tables: reportRows.length
      ? [
          {
            title: "Reports In This Export",
            columns: [
              { id: "report", label: "Report" },
              { id: "scope", label: "Scope" },
              { id: "output", label: "Output" },
            ],
            rows: reportRows,
            maxRows: 8,
          },
        ]
      : [],
    notes: [
      "Redownload regenerates the merged report for the selected historical period.",
      wideTableCount
        ? `${wideTableCount} wide table report${wideTableCount === 1 ? " will" : "s will"} export in landscape.`
        : null,
    ].filter(Boolean),
  };
};
